import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
import QuestionFormats from 'types/QuestionFormats';
// Models
import IQuiz from 'models/Quiz';
import IQuestion from 'models/Question';
import IAnswer, { IAnswerResult } from 'models/Answer';
// Actions
import { setAnswers, setQuestionEnd } from 'store/player/playerSlice';
// Selectors
import { selectQuiz, selectQuestion } from 'store/quiz/quizSelectors';
import { selectQuestionReady, selectIsAnyAnswerCorrect } from 'store/player/playerSelectors';
// Components
import AnswerButton from './AnswerButton';
import TextAnswer from './TextAnswer';
import AnswerDropdown from './AnswerDropdown';
import MultiAnswer from './MultiAnswer';
import GameScoreAnswers from './ScoreAnswers';
import CorrectAnswerDescriptionDialog from 'dialogs/CorrectAnswerDescription.dialog';
import RankingAnswers from './RankingAnswers';

type Props = {
  score: number;
  time: number;
}

const ScoringAnswers:FC<Props> = ({ score, time }) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const question:IQuestion = useAppSelector(selectQuestion);
  const questionReady:boolean = useAppSelector(selectQuestionReady);
  const isAnyAnswerCorrect:boolean = useAppSelector(selectIsAnyAnswerCorrect);

  const { open, toggle } = useToggle();
  const onCloseDialog = () => {
    dispatch(setQuestionEnd(true));
    toggle();
  }

  const handleClick = ({ ref, title, correct }:{ ref:string, title:string, correct?:boolean }) => {
    const showCorrectAnswerDescription = [QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type)
      && question.correctAnswerDescription
      && !isAnyAnswerCorrect;

    const isScoringQuizzes = [QuizTypes.Scoring, QuizTypes.Streak, QuizTypes.BeatTheExpert].includes(quiz.type);
    const isPredictionQuiz = quiz.type === QuizTypes.Prediction;

    const nextAnswer:IAnswerResult = {
      type: question.type,
      score: isAnyAnswerCorrect || correct || isPredictionQuiz ? score : 0,
      correct: isScoringQuizzes && isAnyAnswerCorrect ? true : correct,
      time,
      question: {
        ref: question.ref,
        title: question.title
      },
      input: {
        choice: { ref, title }
      }
    };

    if (showCorrectAnswerDescription) toggle();

    dispatch(setAnswers([nextAnswer]));
    if (!showCorrectAnswerDescription) {
      dispatch(setQuestionEnd(true));
    }
  }

  if ( [QuizTypes.Personality, QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector].includes(quiz.type) ) return null;
  return (
    <Fragment>
      {open ? <CorrectAnswerDescriptionDialog onClose={onCloseDialog} /> : null}

      <div className="py-2 px-4">
        <div className="flex flex-col gap-4 mb-4">
          {question.type === QuestionTypes.ShortText && (
            question.format === QuestionFormats.Score 
              ? <GameScoreAnswers score={score} time={time} disabled={!questionReady} />
              : <TextAnswer score={score} time={time} disabled={!questionReady}  />
          )}
          {question.type === QuestionTypes.MultiChoice && (
            <MultiAnswer answers={question.answers} disabled={!questionReady} score={score} time={time} />
          )}
          {question.type === QuestionTypes.SingleChoice && (
            question.renderAsDropdown ? (
              <AnswerDropdown answers={question.answers} onSubmit={handleClick} disabled={!questionReady} />
            ) : (
              question.answers.map((answer:IAnswer) => (
                <AnswerButton
                  key={`answer-item-${answer.ref}`}
                  answer={answer}
                  onClick={handleClick}
                  disabled={!questionReady}
                />
              ))
            )
          )}
          {question.type === QuestionTypes.Ranking && (
            <RankingAnswers time={time} disabled={!questionReady} />
          )}
        </div>
        {!questionReady && (
          <span className="block text-gray-500 text-md font-bold text-center">
            {question.video
              ? t('pages.question.videoHint') 
              : question.audio
                ? t('pages.question.audioHint')
                : t('pages.question.imageHint')
            }
          </span>
        )}
      </div>
    </Fragment>
  )
}

export default ScoringAnswers;
